
import { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import { cn } from "@/lib/utils";
import { UserPlus, UserMinus, FolderKanban, Settings, CheckCircle } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { format } from "date-fns";
import type { Activity } from "@/types";

interface ActivityFeedProps {
  className?: string;
}

const getActivityIcon = (type: Activity['type'], data?: any) => {
  // Check if this is a client removal activity (which uses new_client type but with removal action)
  if (type === 'new_client' && data?.action === 'removed') {
    return <UserMinus className="w-8 h-8 text-red-400" />;
  }
  
  switch (type) {
    case "new_client":
      return <UserPlus className="w-8 h-8 text-green-400" />;
    case "new_program":
      return <FolderKanban className="w-8 h-8 text-blue-400" />;
    case "program_update":
      return <FolderKanban className="w-8 h-8 text-yellow-400" />;
    case "profile_update":
      return <Settings className="w-8 h-8 text-purple-400" />;
    default:
      return <CheckCircle className="w-8 h-8 text-gray-400" />;
  }
};

export function ActivityFeed({ className }: ActivityFeedProps) {
  const [activities, setActivities] = useState<Activity[]>([]);

  useEffect(() => {
    const fetchActivities = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data, error } = await supabase
        .from('activities')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(20);

      if (error) {
        console.error('Error fetching activities:', error);
        return;
      }

      if (data) {
        setActivities(data as Activity[]);
      }
    };

    fetchActivities();

    const channel = supabase
      .channel('activities-channel')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'activities'
        },
        (payload) => {
          setActivities(prev => [payload.new as Activity, ...prev].slice(0, 20));
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  return (
    <Card className={cn("backdrop-blur-lg bg-white/10 border-white/20", className)}>
      <CardHeader>
        <CardTitle className="text-xl font-semibold text-white">Recent Activity</CardTitle>
      </CardHeader>
      <CardContent>
        <ScrollArea className="h-[300px] pr-4">
          {activities.length === 0 ? (
            <div className="text-center text-white/70 py-8">
              No recent activity to display
            </div>
          ) : (
            <div className="space-y-6">
              {activities.map((activity) => (
                <div
                  key={activity.id}
                  className="flex gap-4 items-start"
                >
                  <div className="flex-shrink-0">
                    {getActivityIcon(activity.type, activity.data)}
                  </div>
                  <div className="flex-1 space-y-1">
                    <p className="text-sm font-medium text-white">
                      {activity.title}
                    </p>
                    <p className="text-sm text-white/70">{activity.description}</p>
                    <p className="text-xs text-white/50">
                      {format(new Date(activity.created_at), 'MMM d, h:mm a')}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </ScrollArea>
      </CardContent>
    </Card>
  );
}
